import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { getPhotoUrl } from "@/utils/photoUrl";
import TeamsTab from "@/components/dashboard/TeamsTab";
import GamePlayersList from "@/components/GamePlayersList";
import EditProfileDialog from "@/components/dashboard/EditProfileDialog";
import ChangePasswordDialog from "@/components/dashboard/ChangePasswordDialog";
import GameCalendarSection from "@/components/dashboard/GameCalendarSection";
import TeammatesSection from "@/components/dashboard/TeammatesSection";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  LogOut,
  Calendar,
  Clock,
  MapPin,
  Users,
  User,
  Trophy,
  Loader2,
  Volleyball,
  Camera,
  Key,
  Save,
  UserPlus,
  UserMinus,
  Edit3,
  Mail,
  Phone,
  Sun,
  Moon,
  CreditCard,
  Timer
} from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useLanguage, LanguageSelectorCompact } from "@/i18n";

// Posições disponíveis
const POSITIONS = ["Ponteiro", "Oposto", "Central", "Levantador", "Líbero"];

// Função para formatar telefone americano
const formatPhone = (value) => {
  if (!value) return "";
  // Remove tudo que não é número
  const numbers = value.replace(/\D/g, "");
  // Aplica a máscara americana (XXX) XXX-XXXX
  if (numbers.length <= 3) {
    return `(${numbers}`;
  } else if (numbers.length <= 6) {
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
  } else if (numbers.length <= 10) {
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6)}`;
  }
  return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
};

// Função para validar email
const isValidEmail = (email) => {
  if (!email) return true; // Email vazio é válido (não obrigatório)
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return regex.test(email);
};

export default function SimplePlayerDashboard({ player, onLogout, onPlayerUpdate }) {
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();
  const [playerGamedays, setPlayerGamedays] = useState([]);
  const [games, setGames] = useState([]);
  const [teammates, setTeammates] = useState({ most_played: [], least_played: [], total_games: 0, unique_teammates: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [currentPlayer, setCurrentPlayer] = useState(player);
  
  // Edit profile states
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editFirstName, setEditFirstName] = useState(player.first_name || '');
  const [editLastName, setEditLastName] = useState(player.last_name || '');
  const [editPosition, setEditPosition] = useState(player.position || '');
  const [editPosition2, setEditPosition2] = useState(player.position2 || '');
  const [editEmail, setEditEmail] = useState(player.email || '');
  const [editPhone, setEditPhone] = useState(player.phone || '');
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [emailError, setEmailError] = useState('');
  
  // Change password states
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  
  // Photo upload states
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  
  // System settings
  const [registrationsEnabled, setRegistrationsEnabled] = useState(false);
  const [gamePayments, setGamePayments] = useState({}); // { gameId: { paid: bool } }
  const [payingGameId, setPayingGameId] = useState(null);
  
  // Calendar state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [allGamedays, setAllGamedays] = useState([]);

  // Calendar modifier styles (extracted to avoid inline re-creation)
  // Note: Must be declared AFTER allGamedays state
  const calendarModifiers = useMemo(() => ({
    hasGame: allGamedays.map(gd => new Date(gd.date || gd.created_at)),
    playerGame: playerGamedays.map(gd => new Date(gd.date || gd.created_at))
  }), [allGamedays, playerGamedays]);

  const calendarModifiersStyles = useMemo(() => ({
    hasGame: { 
      backgroundColor: 'rgba(204, 255, 0, 0.2)',
      borderRadius: '50%'
    },
    playerGame: { 
      backgroundColor: 'rgba(34, 197, 94, 0.3)',
      border: '2px solid #22c55e',
      borderRadius: '50%'
    }
  }), []);
  
  // Player permissions
  const [playerPermissions, setPlayerPermissions] = useState({
    view_profile: true,
    edit_profile: true,
    change_password: true,
    change_photo: true,
    view_calendar: true,
    view_games: true,
    view_teammates: true,
    register_games: true,
    view_history: true,
  });

  // Fetch permissions on mount
  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const response = await axios.get(`${API}/role-permissions`);
        if (response.data.jogador) {
          setPlayerPermissions(response.data.jogador);
        }
      } catch (error) {
        console.error("Error loading permissions:", error);
      }
    };
    loadPermissions();
  }, []);
  
  // Fetch data when player ID is available - run only once
  const fetchData = useCallback(async () => {
    const playerId = player?.player_id || player?.id;
    if (!playerId) return;
    
    setIsLoading(true);
    try {
      const [gamedaysRes, gamesRes, teammatesRes, settingsRes, allGamedaysRes] = await Promise.all([
        axios.get(`${API}/gamedays/player/${playerId}`).catch(() => ({ data: [] })),
        axios.get(`${API}/game-registrations`).catch(() => ({ data: [] })),
        axios.get(`${API}/players/${playerId}/teammates`).catch(() => ({ data: { most_played: [], least_played: [], total_games: 0, unique_teammates: 0 } })),
        axios.get(`${API}/system-settings`).catch(() => ({ data: { registrations_enabled: false } })),
        axios.get(`${API}/gamedays`).catch(() => ({ data: [] }))
      ]);
      
      setPlayerGamedays(gamedaysRes.data?.slice(0, 10) || []);
      setTeammates(teammatesRes.data || { most_played: [], least_played: [], total_games: 0, unique_teammates: 0 });
      setRegistrationsEnabled(settingsRes.data?.registrations_enabled || false);
      setAllGamedays(allGamedaysRes.data || []);
      
      const upcomingGames = (gamesRes.data || [])
        .filter(g => g.is_open || new Date(g.date) >= new Date())
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, 5);
      setGames(upcomingGames);
      
      // Check payment status for each game
      const paymentChecks = {};
      await Promise.all(
        upcomingGames.map(async (g) => {
          try {
            const payRes = await axios.get(`${API}/payments/game/${g.id}/player/${playerId}`);
            paymentChecks[g.id] = payRes.data;
          } catch {
            paymentChecks[g.id] = { paid: false };
          }
        })
      );
      setGamePayments(paymentChecks);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setIsLoading(false);
    }
  }, [player?.player_id, player?.id]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Atualiza os estados de edição quando o player muda
  useEffect(() => {
    setCurrentPlayer(player);
    setEditFirstName(player.first_name || '');
    setEditLastName(player.last_name || '');
    setEditPosition(player.position || '');
    setEditPosition2(player.position2 || '');
    setEditEmail(player.email || '');
    setEditPhone(player.phone || '');
  }, [player]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  // Handle phone input with mask
  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setEditPhone(formatted);
  };

  // Handle email validation
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEditEmail(value);
    if (value && !isValidEmail(value)) {
      setEmailError("Formato de email inválido");
    } else {
      setEmailError("");
    }
  };

  // Handle photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('toasts.formatNotSupported'));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('toasts.fileTooLarge'));
      return;
    }

    setIsUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Use Cloudinary endpoint for persistent storage
      const uploadRes = await axios.post(`${API}/upload/photo/cloudinary`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Update player profile with new photo
      const playerId = player?.player_id || player?.id;
      await axios.patch(`${API}/players/${playerId}/profile`, {
        photo_url: uploadRes.data.photo_url
      });

      setCurrentPlayer(prev => ({ ...prev, photo_url: uploadRes.data.photo_url }));
      if (onPlayerUpdate) {
        onPlayerUpdate({ ...currentPlayer, photo_url: uploadRes.data.photo_url });
      }
      toast.success(t('toasts.photoUploaded'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.errorPhotoUpload'));
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  // Handle profile update
  const handleUpdateProfile = async () => {
    if (!editFirstName.trim()) {
      toast.error(t('toasts.nameRequired'));
      return;
    }

    if (editEmail && !isValidEmail(editEmail)) {
      toast.error(t('toasts.invalidEmail'));
      return;
    }

    setIsUpdatingProfile(true);
    try {
      const res = await axios.patch(`${API}/players/${player?.player_id || player?.id}/profile`, {
        first_name: editFirstName.trim(),
        last_name: editLastName.trim(),
        position: editPosition,
        position2: editPosition2 || null,
        email: editEmail.trim() || null,
        phone: editPhone || null
      });

      const updatedData = {
        ...currentPlayer,
        first_name: editFirstName.trim(),
        last_name: editLastName.trim(),
        position: editPosition,
        position2: editPosition2 || null,
        email: editEmail.trim() || null,
        phone: editPhone || null
      };

      setCurrentPlayer(updatedData);
      if (onPlayerUpdate) {
        onPlayerUpdate(updatedData);
      }
      toast.success(t('toasts.profileUpdated'));
      setShowEditProfile(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.errorUpdatingProfile'));
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  // Handle password change
  const handleChangePassword = async () => {
    if (newPassword.length < 4) {
      toast.error(t('toasts.passwordMinLength'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('toasts.passwordsDoNotMatch'));
      return;
    }

    setIsUpdatingPassword(true);
    try {
      await axios.patch(`${API}/players/${player?.player_id || player?.id}/password`, {
        new_password: newPassword
      });

      toast.success(t('toasts.passwordChanged'));
      setShowChangePassword(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.errorChangingPassword'));
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const getPlayerDisplayName = () => {
    return `${currentPlayer.first_name || ''} ${currentPlayer.last_name || ''}`.trim() || currentPlayer.name || 'Jogador';
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card/80 backdrop-blur-md border-b border-border sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Player Photo with Upload Option */}
              <div className="relative group">
                <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-primary/50 bg-muted">
                  {currentPlayer.photo_url ? (
                    <img 
                      src={getPhotoUrl(currentPlayer.photo_url)}
                      alt={getPlayerDisplayName()}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="h-7 w-7 text-muted-foreground" />
                    </div>
                  )}
                </div>
                {/* Photo upload overlay - only if permission granted */}
                {playerPermissions.change_photo && (
                  <label className="absolute inset-0 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                    {isUploadingPhoto ? (
                      <Loader2 className="h-5 w-5 text-foreground animate-spin" />
                    ) : (
                      <Camera className="h-5 w-5 text-foreground" />
                    )}
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handlePhotoUpload}
                      className="hidden"
                      disabled={isUploadingPhoto}
                      data-testid="photo-upload-input"
                    />
                  </label>
                )}
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground flex items-center gap-2">
                  {getPlayerDisplayName()}
                  {playerPermissions.edit_profile && (
                  <EditProfileDialog
                    open={showEditProfile}
                    onOpenChange={setShowEditProfile}
                    editFirstName={editFirstName}
                    setEditFirstName={setEditFirstName}
                    editLastName={editLastName}
                    setEditLastName={setEditLastName}
                    editPosition={editPosition}
                    setEditPosition={setEditPosition}
                    editPosition2={editPosition2}
                    setEditPosition2={setEditPosition2}
                    editEmail={editEmail}
                    setEditEmail={setEditEmail}
                    editPhone={editPhone}
                    handlePhoneChange={handlePhoneChange}
                    handleEmailChange={handleEmailChange}
                    emailError={emailError}
                    isUpdatingProfile={isUpdatingProfile}
                    handleUpdateProfile={handleUpdateProfile}
                    t={t}
                    trigger={
                      <button className="p-1 hover:bg-white/10 rounded transition-colors" data-testid="edit-profile-btn">
                        <Edit3 className="h-4 w-4 text-muted-foreground" />
                      </button>
                    }
                  />
                  )}
                </h1>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-[#CCFF00]/20 text-[#CCFF00] border-0 text-xs">
                    {currentPlayer.position}
                  </Badge>
                  {currentPlayer.position2 && (
                    <Badge className="bg-blue-500/20 text-blue-400 border-0 text-xs">
                      {currentPlayer.position2}
                    </Badge>
                  )}
                  {currentPlayer.yankee && (
                    <Badge variant="outline" className="text-xs border-border">
                      {currentPlayer.yankee}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Language Selector */}
              <LanguageSelectorCompact className="h-9" />

              {/* Change Password */}
              {playerPermissions.change_password && (
                <ChangePasswordDialog
                  open={showChangePassword}
                  onOpenChange={setShowChangePassword}
                  newPassword={newPassword}
                  setNewPassword={setNewPassword}
                  confirmPassword={confirmPassword}
                  setConfirmPassword={setConfirmPassword}
                  isUpdatingPassword={isUpdatingPassword}
                  handleChangePassword={handleChangePassword}
                  t={t}
                  trigger={
                    <Button variant="outline" size="sm" className="border-border text-foreground hover:bg-muted" data-testid="change-password-btn">
                      <Key className="h-4 w-4 mr-2" />
                      <span className="hidden sm:inline">{t('playerDashboard.changePassword')}</span>
                    </Button>
                  }
                />
              )}

              {/* Theme Toggle */}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => toggleTheme()}
                className="border-border text-foreground hover:bg-muted"
                data-testid="theme-toggle-btn"
              >
                {theme === 'dark' ? (
                  <Sun className="h-4 w-4 text-yellow-400" />
                ) : (
                  <Moon className="h-4 w-4 text-blue-400" />
                )}
              </Button>

              {/* Logout Button */}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onLogout}
                className="border-border text-foreground hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/50"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">{t('nav.logout')}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 max-w-4xl">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-[#CCFF00]" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Player Info Card */}
            <Card className="bg-card/50 border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-foreground text-lg">
                  <User className="h-5 w-5 text-[#CCFF00]" />
                  {t('playerDashboard.myInfo')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {currentPlayer.email && (
                    <div className="flex items-center gap-2 text-foreground">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm truncate">{currentPlayer.email}</span>
                    </div>
                  )}
                  {currentPlayer.phone && (
                    <div className="flex items-center gap-2 text-foreground">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{currentPlayer.phone}</span>
                    </div>
                  )}
                  {!currentPlayer.email && !currentPlayer.phone && (
                    <p className="text-muted-foreground text-sm col-span-2">
                      {t('playerDashboard.noContactInfo')}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Stats Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-card/50 border-border/50">
                <CardContent className="p-4 text-center">
                  <Trophy className="h-6 w-6 text-[#CCFF00] mx-auto mb-2" />
                  <p className="text-2xl font-bold text-foreground">{teammates.total_games || 0}</p>
                  <p className="text-xs text-muted-foreground">{t('playerDashboard.games')}</p>
                </CardContent>
              </Card>
              <Card className="bg-card/50 border-border/50">
                <CardContent className="p-4 text-center">
                  <Users className="h-6 w-6 text-blue-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-foreground">{teammates.unique_teammates || 0}</p>
                  <p className="text-xs text-muted-foreground">{t('playerDashboard.teammates')}</p>
                </CardContent>
              </Card>
              <Card className="bg-card/50 border-border/50">
                <CardContent className="p-4 text-center">
                  <Calendar className="h-6 w-6 text-green-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-foreground">{playerGamedays.length}</p>
                  <p className="text-xs text-muted-foreground">{t('playerDashboard.lineups')}</p>
                </CardContent>
              </Card>
              <Card className="bg-card/50 border-border/50">
                <CardContent className="p-4 text-center">
                  <Volleyball className="h-6 w-6 text-orange-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-foreground">{currentPlayer.position}</p>
                  <p className="text-xs text-muted-foreground">{t('login.position')}</p>
                </CardContent>
              </Card>
            </div>

            {/* Próximos Jogos - Inscrições */}
            {games.length > 0 && (
              <Card className="bg-card/50 border-border/50">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-foreground text-lg">
                    <Calendar className="h-5 w-5 text-[#CCFF00]" />
                    {t('playerDashboard.upcomingGames')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {games.map((game) => {
                      const playerId = player?.player_id || player?.id;
                      const isRegistered = game.registered_players?.some(rp => rp.player_id === playerId);
                      const isWaitlisted = game.waitlist?.some(wp => wp.player_id === playerId);
                      const isSignedUp = isRegistered || isWaitlisted;
                      const registeredCount = game.registered_players?.length || 0;
                      const waitlistCount = game.waitlist?.length || 0;
                      const maxPlayers = game.max_players || 18;
                      const isFull = registeredCount >= maxPlayers;
                      const memberType = player?.member_type || currentPlayer?.member_type || 'membro';
                      const hasPaid = gamePayments[game.id]?.paid;
                      
                      return (
                        <div 
                          key={game.id} 
                          className={`p-4 rounded-lg border ${
                            isRegistered 
                              ? 'bg-[#CCFF00]/10 border-[#CCFF00]/30' 
                              : isWaitlisted
                                ? 'bg-amber-500/10 border-amber-500/30'
                                : 'bg-muted/50 border-border/50'
                          }`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="space-y-1">
                              <h4 className="font-semibold text-foreground">{game.title || t('playerDashboard.gameOfDay') || 'Jogo do Dia'}</h4>
                              <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3.5 w-3.5" />
                                  {new Date(game.date + 'T00:00:00').toLocaleDateString()}
                                </span>
                                {game.time && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3.5 w-3.5" />
                                    {game.time}
                                  </span>
                                )}
                                {game.location && (
                                  <a
                                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(game.location)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-primary hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                    data-testid={`game-location-link-${game.id}`}
                                  >
                                    <MapPin className="h-3.5 w-3.5" />
                                    {game.location}
                                  </a>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <Users className="h-3.5 w-3.5 text-muted-foreground" />
                                <span className={`text-sm ${isFull ? 'text-red-400' : 'text-muted-foreground'}`}>
                                  {registeredCount}/{maxPlayers} {t('playerDashboard.registered')}
                                </span>
                                {waitlistCount > 0 && (
                                  <span className="text-xs text-amber-400">+{waitlistCount} espera</span>
                                )}
                                {isRegistered && (
                                  <Badge className="bg-[#CCFF00]/20 text-[#CCFF00] border-0 text-xs">
                                    {t('playerDashboard.registered')}
                                  </Badge>
                                )}
                                {isWaitlisted && (
                                  <Badge className="bg-amber-500/20 text-amber-400 border-0 text-xs">
                                    Lista de Espera
                                  </Badge>
                                )}
                                {hasPaid && (
                                  <Badge className="bg-green-500/20 text-green-400 border-0 text-xs">
                                    Pago
                                  </Badge>
                                )}
                              </div>
                              {/* Payment deadline warning */}
                              {isRegistered && !hasPaid && (() => {
                                const myReg = game.registered_players?.find(rp => rp.player_id === playerId);
                                const deadline = myReg?.payment_deadline ? new Date(myReg.payment_deadline) : null;
                                if (!deadline) return null;
                                const hoursLeft = Math.max(0, Math.round((deadline - new Date()) / 3600000));
                                return (
                                  <p className={`text-xs mt-1 flex items-center gap-1 ${hoursLeft <= 6 ? 'text-red-400' : 'text-amber-400'}`}>
                                    <Timer className="h-3 w-3" />
                                    {hoursLeft > 0 
                                      ? `Pague em ate ${hoursLeft}h ou sua vaga sera liberada`
                                      : 'Prazo de pagamento expirando - pague agora!'}
                                  </p>
                                );
                              })()}
                            </div>
                            
                            <div className="flex items-center gap-2">
                              {/* Payment button - shown when registered and not yet paid */}
                              {isRegistered && !hasPaid && (
                                <Button
                                  size="sm"
                                  data-testid={`game-pay-btn-${game.id}`}
                                  onClick={async () => {
                                    setPayingGameId(game.id);
                                    try {
                                      const res = await axios.post(`${API}/payments/stripe/checkout`, {
                                        package_id: "jogo_avulso",
                                        player_id: playerId,
                                        game_id: game.id,
                                        origin_url: window.location.origin
                                      });
                                      if (res.data.checkout_url) {
                                        window.open(res.data.checkout_url, '_blank');
                                        toast.success("Redirecionando para pagamento...");
                                      }
                                    } catch (error) {
                                      toast.error(error.response?.data?.detail || "Erro ao iniciar pagamento");
                                    } finally {
                                      setPayingGameId(null);
                                    }
                                  }}
                                  className="bg-green-600 text-white hover:bg-green-700"
                                  disabled={payingGameId === game.id}
                                >
                                  {payingGameId === game.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                  ) : (
                                    <CreditCard className="h-4 w-4 mr-1" />
                                  )}
                                  R$13
                                </Button>
                              )}

                              {/* Register/Unregister button */}
                              {game.is_open && (
                                <Button
                                  size="sm"
                                  data-testid={`game-register-btn-${game.id}`}
                                  onClick={async () => {
                                    try {
                                      if (isSignedUp) {
                                        if (!confirm(t('playerDashboard.confirmUnregister') || "Tem certeza que deseja cancelar sua inscricao?")) return;
                                        await axios.post(`${API}/game-registrations/${game.id}/cancel`, {
                                          player_id: playerId
                                        });
                                        toast.success(t('playerDashboard.unregisterSuccess') || "Inscricao cancelada");
                                      } else {
                                        const res = await axios.post(`${API}/game-registrations/${game.id}/register`, {
                                          player_id: playerId
                                        });
                                        if (res.data.status === 'waitlist') {
                                          toast.info(res.data.message || "Adicionado a lista de espera");
                                        } else {
                                          toast.success(t('playerDashboard.registerSuccess') || "Inscricao confirmada!");
                                        }
                                      }
                                      fetchData();
                                    } catch (error) {
                                      toast.error(error.response?.data?.detail || t('common.error'));
                                    }
                                  }}
                                  className={isSignedUp 
                                    ? "bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30" 
                                    : "bg-[#CCFF00] text-black hover:bg-[#CCFF00]/90"
                                  }
                                >
                                  {isSignedUp 
                                    ? (t('playerDashboard.unregister') || 'Cancelar') 
                                    : memberType === 'convidado' 
                                      ? 'Entrar na Espera' 
                                      : (t('playerDashboard.register') || 'Inscrever')
                                  }
                                </Button>
                              )}
                              
                              {!game.is_open && (
                                <Badge className="bg-muted text-muted-foreground border-0">
                                  {t('playerDashboard.registrationsClosed') || 'Inscricoes fechadas'}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Lista de Inscritos no Próximo Jogo */}
            <GamePlayersList />

            {/* Calendario de Jogos */}
            <GameCalendarSection
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              allGamedays={allGamedays}
              playerGamedays={playerGamedays}
              calendarModifiers={calendarModifiers}
              calendarModifiersStyles={calendarModifiersStyles}
              t={t}
            />

            {/* Teammates */}
            <TeammatesSection teammates={teammates} t={t} />


            {/* Meus Times - Histórico */}
            <TeamsTab
              playerGamedays={playerGamedays}
              playerId={player?.player_id || player?.id}
              formatDate={formatDate}
              t={t}
            />
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-card/50 border-t border-border/50 py-6 mt-8">
        <p className="text-center text-muted-foreground text-sm">
          VolleyxBrasil © {new Date().getFullYear()}
        </p>
      </footer>
    </div>
  );
}
